import React from 'react';

// Explanatory diagrams for the landing page. Inline SVG drawn with the theme's
// CSS variables, so they follow light/dark mode with no image assets.
// They illustrate mechanisms only — never prices, returns or account data.

const C = {
  fg: 'hsl(var(--foreground))',
  muted: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  card: 'hsl(var(--card))',
  primary: 'hsl(var(--primary))',
  win: 'hsl(var(--win))',
  loss: 'hsl(var(--loss))',
};

const text = { fontFamily: 'inherit' };

function Arrowhead({ id, color }) {
  return (
    <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
    </marker>
  );
}

/** Stops are GTTs held at the broker, so they fire even with our server down. */
export function BrokerStopsDiagram({ className }) {
  return (
    <svg viewBox="0 0 400 280" className={className} role="img" aria-labelledby="broker-stops-title" style={text}>
      <title id="broker-stops-title">
        Stop-loss and target orders are held at Zerodha as GTT orders, so they still trigger on NSE when the Jigri server is offline.
      </title>
      <defs>
        <Arrowhead id="bs-arrow-muted" color={C.muted} />
        <Arrowhead id="bs-arrow-primary" color={C.primary} />
      </defs>

      {/* Jigri server — offline */}
      <rect x="16" y="24" width="140" height="64" rx="10" fill={C.card} stroke={C.border} strokeWidth="1.5" />
      <text x="86" y="52" textAnchor="middle" fontSize="14" fontWeight="600" fill={C.fg}>Jigri server</text>
      <text x="86" y="72" textAnchor="middle" fontSize="13" fill={C.loss}>offline</text>
      <circle cx="152" cy="26" r="11" fill={C.loss} />
      <path d="M 147 21 L 157 31 M 157 21 L 147 31" stroke={C.card} strokeWidth="2.2" strokeLinecap="round" />

      {/* Orders were placed earlier */}
      <line x1="160" y1="56" x2="240" y2="56" stroke={C.muted} strokeWidth="1.5" strokeDasharray="5 4" markerEnd="url(#bs-arrow-muted)" />
      <text x="200" y="46" textAnchor="middle" fontSize="11" fill={C.muted}>placed earlier</text>

      {/* Broker */}
      <rect x="244" y="24" width="140" height="64" rx="10" fill={C.card} stroke={C.border} strokeWidth="1.5" />
      <text x="314" y="52" textAnchor="middle" fontSize="14" fontWeight="600" fill={C.fg}>Zerodha</text>
      <text x="314" y="72" textAnchor="middle" fontSize="13" fill={C.muted}>your broker</text>

      {/* GTTs held at the broker */}
      {[{ y: 102, label: 'Stop-loss GTT' }, { y: 144, label: 'Target GTT' }].map(({ y, label }) => (
        <g key={label}>
          <rect x="244" y={y} width="140" height="34" rx="8" fill={C.primary} fillOpacity="0.1" stroke={C.primary} strokeWidth="1.2" />
          <text x="258" y={y + 22} fontSize="13" fontWeight="600" fill={C.fg}>{label}</text>
          <circle cx="366" cy={y + 17} r="5" fill={C.win} />
        </g>
      ))}

      {/* Exchange */}
      <line x1="314" y1="182" x2="314" y2="210" stroke={C.primary} strokeWidth="1.8" markerEnd="url(#bs-arrow-primary)" />
      <text x="304" y="200" textAnchor="end" fontSize="12" fill={C.muted}>fires at your level</text>
      <rect x="244" y="214" width="140" height="48" rx="10" fill={C.card} stroke={C.border} strokeWidth="1.5" />
      <text x="314" y="243" textAnchor="middle" fontSize="14" fontWeight="600" fill={C.fg}>NSE</text>

      <text x="16" y="236" fontSize="13" fill={C.muted}>Nothing on our side</text>
      <text x="16" y="254" fontSize="13" fill={C.muted}>needs to be running.</text>
    </svg>
  );
}

/** The backtest's "today" fences off everything published after it. */
export function LookAheadDiagram({ className }) {
  const today = 220;
  return (
    <svg viewBox="0 0 400 220" className={className} role="img" aria-labelledby="look-ahead-title" style={text}>
      <title id="look-ahead-title">
        On a simulated 15 April, the backtest can read prices up to 15 April but not results published on 25 April; everything after its date is blocked.
      </title>
      <defs>
        <pattern id="la-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke={C.muted} strokeOpacity="0.25" strokeWidth="2" />
        </pattern>
      </defs>

      {/* Visible past vs blocked future */}
      <rect x="20" y="36" width={today - 20} height="124" fill={C.win} fillOpacity="0.06" />
      <rect x={today} y="36" width={380 - today} height="124" fill="url(#la-hatch)" />
      <text x="120" y="56" textAnchor="middle" fontSize="12" fontWeight="600" fill={C.win}>visible</text>
      <text x="300" y="56" textAnchor="middle" fontSize="12" fontWeight="600" fill={C.muted}>future — reads blocked</text>

      {/* Time axis */}
      <line x1="20" y1="120" x2="380" y2="120" stroke={C.border} strokeWidth="2" />

      {/* Quarter end */}
      <circle cx="70" cy="120" r="6" fill={C.fg} />
      <text x="70" y="100" textAnchor="middle" fontSize="13" fontWeight="600" fill={C.fg}>Quarter ends</text>
      <text x="70" y="142" textAnchor="middle" fontSize="12" fill={C.muted}>31 Mar</text>

      {/* Results published — not yet visible */}
      <circle cx="310" cy="120" r="6" fill={C.card} stroke={C.muted} strokeWidth="2" />
      <text x="310" y="100" textAnchor="middle" fontSize="13" fill={C.muted}>Results published</text>
      <text x="310" y="142" textAnchor="middle" fontSize="12" fill={C.muted}>25 Apr</text>

      {/* Backtest "today" */}
      <line x1={today} y1="32" x2={today} y2="170" stroke={C.primary} strokeWidth="2" strokeDasharray="5 4" />
      <text x={today} y="188" textAnchor="middle" fontSize="13" fontWeight="600" fill={C.primary}>Backtest “today”</text>
      <text x={today} y="205" textAnchor="middle" fontSize="12" fill={C.muted}>15 Apr</text>
    </svg>
  );
}
